/**
 * Agrupamento VISUAL de cards do mesmo assunto.
 *
 * Até 07/08/2026 "mesmo assunto" era trava: o quadro se recusava a abrir o
 * segundo card do mesmo tema, e o publicador gravava a matéria nova por cima da
 * antiga. Isso destruiu ~114 matérias em 6 dias e a trava foi removida de todas
 * as etapas — notícia factual não tem limite por assunto.
 *
 * O que ficou é só INFORMAÇÃO: cards do mesmo assunto acendem uma luz da mesma
 * cor e ganham borda fina, para quem olha o quadro VER que estão juntos. Trinta
 * matérias sobre GTA 6 no mesmo dia aparecem agrupadas — e todas as trinta são
 * publicáveis. Agrupar mostra; travar impedia.
 */

// Palavras que não carregam assunto. Espelha a intenção do `_STOP_TEMA` do
// backend: sem elas, "argentina x egito" e "egito e argentina" não se juntariam.
const VAZIAS = new Set([
  'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas', 'de', 'do', 'da', 'dos',
  'das', 'em', 'no', 'na', 'nos', 'nas', 'por', 'para', 'pra', 'com', 'sem',
  'sob', 'sobre', 'e', 'ou', 'que', 'se', 'ao', 'aos', 'hoje', 'agora', 'ontem',
  'seu', 'sua', 'seus', 'suas', 'ele', 'ela', 'eles', 'elas', 'isso', 'este',
  'esta', 'esse', 'essa', 'foi', 'sera', 'vai', 'vao', 'como', 'quando', 'onde',
  'confira', 'veja', 'saiba', 'entenda', 'apos', 'contra',
]);

/**
 * A assinatura do assunto: minúsculas, sem acento, sem palavra vazia, tokens
 * ORDENADOS. Ordenar é o que faz "Argentina x Egito" e "Egito e Argentina"
 * caírem no mesmo grupo.
 *
 * Número de UM dígito é preservado de propósito: descartá-lo por ser curto faria
 * "GTA 6" e "GTA 5" virarem o mesmo assunto — dois jogos diferentes na mesma luz.
 */
export function assinaturaDeAssunto(tema: string): string {
  const limpo = (tema ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  const tokens = (limpo.match(/[a-z0-9]+/g) ?? []).filter(
    (t) => !VAZIAS.has(t) && (t.length > 1 || /\d/.test(t)),
  );
  return [...new Set(tokens)].sort().join('-');
}

/**
 * Cores das luzes. Escolhidas em HSL com saturação e luminosidade médias para
 * ficarem legíveis no tema claro E no escuro, sem precisar de duas paletas.
 */
const CORES = [
  'hsl(210 70% 55%)', // azul
  'hsl(150 55% 45%)', // verde
  'hsl(275 60% 62%)', // violeta
  'hsl( 25 75% 55%)', // laranja
  'hsl(190 60% 45%)', // ciano
  'hsl(330 60% 60%)', // rosa
  'hsl( 55 60% 45%)', // oliva
  'hsl(  0 60% 58%)', // vermelho tijolo
];

/**
 * Mapa `id do card → cor`, só para cards que TÊM companhia no assunto.
 *
 * Card sozinho não recebe cor: pintar todo mundo transformaria a pista em ruído,
 * e a informação que interessa é "este tem irmãos". As assinaturas são ordenadas
 * antes de receber cor para que a mesma tela dê sempre a mesma cor, independente
 * da ordem em que as raias devolveram os cards.
 */
export function gruposDeAssunto(
  cards: { id: number; tema?: string | null }[],
): Map<number, string> {
  const porAssinatura = new Map<string, number[]>();
  for (const c of cards) {
    const chave = assinaturaDeAssunto(c.tema ?? '');
    if (!chave) continue; // sem assunto computável não agrupa nem com outro igual
    const lista = porAssinatura.get(chave);
    if (lista) lista.push(c.id);
    else porAssinatura.set(chave, [c.id]);
  }

  const out = new Map<number, string>();
  const comCompanhia = [...porAssinatura.entries()]
    .filter(([, ids]) => ids.length > 1)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  comCompanhia.forEach(([, ids], i) => {
    const cor = CORES[i % CORES.length];
    for (const id of ids) out.set(id, cor);
  });
  return out;
}
